// Sudoku puzzle generator and solver
export class SudokuGenerator {
  constructor() {
    this.grid = [];
    this.solution = [];
  }

  // Generate a complete valid Sudoku grid
  generateComplete() {
    this.grid = Array(9)
      .fill(null)
      .map(() => Array(9).fill(0));
    this.fillGrid(0, 0);
    return this.grid.map((row) => [...row]);
  }

  // Recursive backtracking to fill the grid
  fillGrid(row, col) {
    if (row === 9) return true;
    if (col === 9) return this.fillGrid(row + 1, 0);

    const numbers = this.shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]);

    for (const num of numbers) {
      if (this.isValid(row, col, num)) {
        this.grid[row][col] = num;
        if (this.fillGrid(row, col + 1)) return true;
        this.grid[row][col] = 0;
      }
    }

    return false;
  }

  // Check if a number is valid at a position
  isValid(row, col, num) {
    // Check row
    if (this.grid[row].includes(num)) return false;

    // Check column
    for (let i = 0; i < 9; i++) {
      if (this.grid[i][col] === num) return false;
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (this.grid[boxRow + i][boxCol + j] === num) return false;
      }
    }

    return true;
  }

  // Shuffle array using Fisher-Yates
  shuffle(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Generate a puzzle by removing numbers
  generate(difficulty = "medium") {
    this.solution = this.generateComplete();
    const puzzle = this.solution.map((row) => [...row]);

    // Number of cells to remove based on difficulty
    const removeCount =
      {
        easy: 35,
        medium: 45,
        hard: 55,
      }[difficulty] || 45;

    let removed = 0;
    const positions = this.shuffle(
      Array.from({ length: 81 }, (_, i) => [Math.floor(i / 9), i % 9]),
    );

    for (const [row, col] of positions) {
      if (removed >= removeCount) break;

      const temp = puzzle[row][col];
      puzzle[row][col] = 0;

      // For easy/medium, we just remove cells
      // For hard, we could add uniqueness checking (skipped for performance)
      removed++;
    }

    return {
      puzzle: puzzle,
      solution: this.solution,
    };
  }

  // Validate if the current state matches the solution
  static validate(current, solution) {
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        if (current[i][j] !== solution[i][j]) return false;
      }
    }
    return true;
  }

  // Check if a move is correct
  static isCorrect(row, col, num, solution) {
    return solution[row][col] === num;
  }

  // Check for conflicts in the grid
  static hasConflict(grid, row, col, num) {
    if (num === 0) return false;

    // Check row
    for (let c = 0; c < 9; c++) {
      if (c !== col && grid[row][c] === num) return true;
    }

    // Check column
    for (let r = 0; r < 9; r++) {
      if (r !== row && grid[r][col] === num) return true;
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const nr = boxRow + r;
        const nc = boxCol + c;
        if (nr !== row && nc !== col && grid[nr][nc] === num) return true;
      }
    }

    return false;
  }

  // Count remaining numbers
  static getRemainingCounts(grid) {
    const counts = {};
    for (let n = 1; n <= 9; n++) counts[n] = 9;

    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        const num = grid[r][c];
        if (num > 0) counts[num]--;
      }
    }

    return counts;
  }

  // Get a hint (returns position and correct number)
  static getHint(current, solution, fixed) {
    const emptyCells = [];

    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (!fixed[r][c] && current[r][c] !== solution[r][c]) {
          emptyCells.push({ row: r, col: c, num: solution[r][c] });
        }
      }
    }

    if (emptyCells.length === 0) return null;

    return emptyCells[Math.floor(Math.random() * emptyCells.length)];
  }
}
